import { isAbortError } from '../api/warehouse.api';
import { useLoad } from './useLoad';

export interface TabCounts<K extends string> {
  /** null until the first answer; a key missing = its count failed (badge hidden). */
  counts: Partial<Record<K, number>> | null;
  reload: () => void;
}

/**
 * Tab badge counts for lists without a summary endpoint: one `limit=1`
 * request per tab key, reading `pagination.total`. A failed key is left out
 * (its badge is simply hidden); the rest still show. Built on `useLoad`, so
 * a hub/filter switch aborts the previous round.
 */
export function useTabCounts<K extends string>(
  keys: readonly K[],
  count: (key: K, signal: AbortSignal) => Promise<number>,
  deps: unknown[],
  enabled = true
): TabCounts<K> {
  const load = useLoad(
    async (signal) => {
      const pairs = await Promise.all(
        keys.map((k) =>
          count(k, signal)
            .then((n) => [k, n] as const)
            .catch((err) => {
              if (isAbortError(err)) throw err;
              return [k, undefined] as const;
            })
        )
      );
      const out: Partial<Record<K, number>> = {};
      pairs.forEach(([k, n]) => {
        if (typeof n === 'number') out[k] = n;
      });
      return out;
    },
    deps,
    enabled
  );
  return { counts: load.data, reload: load.reload };
}
